import { Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ReusableDataGrid } from "../components/shared/ReusableDataGrid";
import dischargeService from "../services/discharge.service";

interface DischargeRow {
  id: string;
  dischargeDate: string;
  riskLevel: "Low" | "Medium" | "High";
  nextFollowUpDue: string;
  specialty: string;
  careManager: string;
  status: "Active" | "Completed";
}

const riskColors: Record<string, { bg: string; color: string }> = {
  Low: { bg: "rgba(31, 181, 81, 0.1)", color: "#1FB551" },
  Medium: { bg: "rgba(255, 169, 77, 0.1)", color: "#FFA94D" },
  High: { bg: "rgba(255, 77, 77, 0.1)", color: "#FF4D4D" },
};

const statusColors: Record<string, { bg: string; color: string }> = {
  Active: { bg: "rgba(0, 123, 255, 0.1)", color: "#007BFF" },
  Completed: { bg: "rgba(31, 181, 81, 0.1)", color: "#1FB551" },
};

export default function DischargeTable() {
  const [data, setData] = useState<DischargeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDischarges = async () => {
      try {
        setLoading(true);
        const discharges = await dischargeService.getAllDischarges();
        const formattedData: DischargeRow[] = discharges.map((discharge: any) => ({
          id: discharge.id,
          dischargeDate: new Date(discharge.discharge_date).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          riskLevel: discharge.risk_level as "Low" | "Medium" | "High",
          nextFollowUpDue: discharge.next_followup_due 
            ? new Date(discharge.next_followup_due).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              })
            : "-",
          specialty: discharge.specialty || "-",
          careManager: discharge.care_manager || "-",
          status: discharge.status as "Active" | "Completed",
        }));
        
        setData(formattedData);
      } catch (error) {
        console.error("Failed to fetch discharges:", error);
        toast.error("Failed to load discharge records");
      } finally {
        setLoading(false);
      }
    };

    fetchDischarges();
  }, []);

  const columns: GridColDef[] = [
    { field: "dischargeDate", headerName: "Discharge Date", width: 150 },
    {
      field: "riskLevel",
      headerName: "Risk Level",
      width: 130,
      renderCell: (params) => {
        const colors = riskColors[params.value as string] || {
          bg: "#eee",
          color: "#333",
        };
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              backgroundColor: colors.bg,
              color: colors.color,
              fontWeight: 500,
            }}
          />
        );
      },
    },
    { field: "nextFollowUpDue", headerName: "Next Follow-up Due", width: 180 },
    { field: "specialty", headerName: "Specialty", width: 150 },
    { field: "careManager", headerName: "Care Manager", width: 180 },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const colors = statusColors[params.value as string] || {
          bg: "#eee",
          color: "#333",
        };
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              backgroundColor: colors.bg,
              color: colors.color,
              fontWeight: 500,
            }}
          />
        );
      },
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
        <div>Loading discharge records...</div>
      </div>
    );
  }
  return (
    <div style={{ height: 500, width: "100%" }}>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>Discharge</h3>
        <p style={{ margin: '8px 0 0 0', color: '#666' }}>List</p>
      </div>
      <ReusableDataGrid<DischargeRow>
        columns={columns}
        data={data}
        onSelectionChange={() => {}}
        getRowId={(row: DischargeRow) => row.id}
        onRowClick={(params) =>
          navigate(`/user-layout/discharge/${params.row.id}`)
        }
      />
    </div>
  );
}
